import { Application, Container, Ticker } from 'pixi.js';
import { isTouchDevice } from '../core/utils';
import { Npc, getNpcConfigs } from './npc';

const getNpcCount = (): number => (isTouchDevice() ? 3 : 9);
const WALK_SPEED = 1.5;
const RUN_SPEED = 3;
const ATTACK_RANGE = 60;
const ATTACK_Y_THRESHOLD = 30;
const DETECT_RANGE = 500;
const RESPAWN_DELAY = 4000;
const MARGIN_TOP = 600;
const MARGIN_BOTTOM = 60;
const DIRECTION_CHANGE_CHANCE = 0.01;
const IDLE_CHANCE = 0.001;
const WALK_CHANCE = 0.05;
const VY_FACTOR = 0.4;

interface NpcEntry {
  npc: Npc;
  respawnTimer: number | null;
  target: NpcEntry | null;
  viewportTarget?: { x: number; y: number };
  hp: number;
  vy: number;
}

export class NpcManager {
  private app: Application;
  private worldContainer: Container;
  private worldHeight: number;
  private entries: NpcEntry[] = [];
  private tickerFn: ((ticker: Ticker) => void) | null = null;

  constructor(app: Application, worldContainer: Container, worldHeight: number) {
    this.app = app;
    this.worldContainer = worldContainer;
    this.worldHeight = worldHeight;
  }

  async init(): Promise<void> {
    const configs = getNpcConfigs();

    for (let i = 0; i < getNpcCount(); i++) {
      const config = configs[i % configs.length];
      const npc = new Npc(config);
      await npc.loadAnimations();

      const x = Math.random() * this.app.screen.width;
      const y = this.getRandomY();
      npc.reset(x, y);
      npc.direction = Math.random() > 0.5 ? 1 : -1;

      this.worldContainer.addChild(npc.container);
      this.entries.push({ npc, respawnTimer: null, target: null, hp: 3, vy: 0 });
    }

    this.tickerFn = (ticker: Ticker) => this.update(ticker.deltaTime);
    this.app.ticker.add(this.tickerFn);
  }

  private getRandomY(): number {
    const minY = Math.min(MARGIN_TOP, this.worldHeight * 0.25);
    const maxY = this.worldHeight - MARGIN_BOTTOM;
    return minY + Math.random() * Math.max(0, maxY - minY);
  }

  private update(delta: number): void {
    for (const entry of this.entries) {
      if (!entry.npc.alive) {
        if (!entry.respawnTimer) {
          entry.respawnTimer = window.setTimeout(() => {
            this.respawn(entry);
          }, RESPAWN_DELAY);
        }
        continue;
      }

      if (entry.npc.state === 'attack' || entry.npc.state === 'death') continue;

      if (isTouchDevice() && !this.isNearViewport(entry.npc)) {
        if (this.moveToViewport(entry, delta)) {
          this.updateMovement(entry, delta);
          this.clampPosition(entry);
          continue;
        }
      }

      this.updateAI(entry, delta);
      this.updateMovement(entry, delta);
      this.clampPosition(entry);
    }
  }

  private isNearViewport(npc: Npc): boolean {
    const viewportTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    return npc.container.y >= viewportTop - 200 && npc.container.y <= viewportBottom + 200;
  }

  private moveToViewport(entry: NpcEntry, _delta: number): boolean {
    const npc = entry.npc;
    const viewportTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const viewportCenterY = viewportTop + viewportHeight / 2;

    const targetStale = entry.viewportTarget &&
      (entry.viewportTarget.y < viewportTop - 200 || entry.viewportTarget.y > viewportBottom + 200);

    if (!entry.viewportTarget || targetStale) {
      entry.viewportTarget = {
        x: Math.random() * this.app.screen.width,
        y: viewportCenterY + (Math.random() - 0.5) * viewportHeight * 0.6,
      };
    }

    const target = entry.viewportTarget;
    const dx = target.x - npc.container.x;
    const dy = target.y - npc.container.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 10) {
      entry.viewportTarget = undefined;
      npc.vx = 0;
      entry.vy = 0;
      return false;
    }

    const speed = RUN_SPEED * 2;
    npc.vx = (dx / distance) * speed;
    entry.vy = (dy / distance) * speed;
    npc.setState('run');
    return true;
  }

  private updateAI(entry: NpcEntry, _delta: number): void {
    const { npc } = entry;

    const enemy = this.findNearestEnemy(entry);
    entry.target = enemy;

    if (enemy) {
      const dx = enemy.npc.container.x - npc.container.x;
      const dy = enemy.npc.container.y - npc.container.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const onSameLevel = absDy < ATTACK_Y_THRESHOLD;

      if (absDx < ATTACK_RANGE && onSameLevel) {
        npc.vx = 0;
        entry.vy = 0;
        npc.direction = dx > 0 ? 1 : -1;
        npc.setState('attack');
        this.dealDamage(enemy);
        return;
      }

      if (absDx < ATTACK_RANGE && !onSameLevel) {
        npc.vx = (dx > 0 ? 1 : -1) * WALK_SPEED * 0.5;
        entry.vy = dy > 0 ? RUN_SPEED * VY_FACTOR : -RUN_SPEED * VY_FACTOR;
        npc.setState('run');
        return;
      }

      npc.vx = dx > 0 ? RUN_SPEED : -RUN_SPEED;
      entry.vy = !onSameLevel ? (dy > 0 ? RUN_SPEED * VY_FACTOR : -RUN_SPEED * VY_FACTOR) : 0;
      npc.setState('run');
      return;
    }

    if (npc.state === 'idle' && Math.random() < WALK_CHANCE) {
      npc.vx = (Math.random() > 0.5 ? 1 : -1) * WALK_SPEED;
      entry.vy = (Math.random() - 0.5) * WALK_SPEED * VY_FACTOR * 2;
      npc.setState('walk');
    } else if (npc.state === 'walk') {
      if (Math.random() < IDLE_CHANCE) {
        npc.vx = 0;
        entry.vy = 0;
        npc.setState('idle');
      } else if (Math.random() < DIRECTION_CHANGE_CHANCE) {
        npc.vx = -npc.vx;
        entry.vy = (Math.random() - 0.5) * WALK_SPEED * VY_FACTOR * 2;
      }
    }
  }

  private updateMovement(entry: NpcEntry, delta: number): void {
    const { npc } = entry;
    if (npc.vx !== 0) {
      npc.container.x += npc.vx * delta;
    }
    if (entry.vy !== 0) {
      npc.container.y += entry.vy * delta;
    }
  }

  private clampPosition(entry: NpcEntry): void {
    const { npc } = entry;
    const w = this.app.screen.width;
    const minY = Math.min(MARGIN_TOP, this.worldHeight * 0.25);
    const maxY = this.worldHeight - MARGIN_BOTTOM;

    if (npc.container.x < 0) {
      npc.container.x = 0;
      npc.vx = Math.abs(npc.vx);
    } else if (npc.container.x > w) {
      npc.container.x = w;
      npc.vx = -Math.abs(npc.vx);
    }

    if (npc.container.y < minY) {
      npc.container.y = minY;
      entry.vy = Math.abs(entry.vy);
    } else if (npc.container.y > maxY) {
      npc.container.y = maxY;
      entry.vy = -Math.abs(entry.vy);
    }
  }

  private findNearestEnemy(entry: NpcEntry): NpcEntry | null {
    let nearest: NpcEntry | null = null;
    let minDist = DETECT_RANGE;

    for (const other of this.entries) {
      if (other === entry || !other.npc.alive) continue;
      const dx = other.npc.container.x - entry.npc.container.x;
      const dy = other.npc.container.y - entry.npc.container.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) {
        minDist = dist;
        nearest = other;
      }
    }

    return nearest;
  }

  private dealDamage(target: NpcEntry): void {
    target.hp -= 1;
    if (target.hp <= 0) {
      target.npc.die();
    }
  }

  private respawn(entry: NpcEntry): void {
    entry.respawnTimer = null;
    entry.hp = 3;
    entry.vy = 0;

    const x = Math.random() * this.app.screen.width;
    const y = this.getRandomY();
    entry.npc.reset(x, y);
    entry.npc.direction = Math.random() > 0.5 ? 1 : -1;
  }

  setWorldHeight(worldHeight: number): void {
    this.worldHeight = worldHeight;
  }

  onResize(): void {
    const maxY = this.worldHeight - MARGIN_BOTTOM;
    for (const entry of this.entries) {
      if (entry.npc.alive && entry.npc.container.y > maxY) {
        entry.npc.container.y = maxY;
      }
    }
  }

  dispose(): void {
    if (this.tickerFn) {
      this.app.ticker.remove(this.tickerFn);
    }
    for (const entry of this.entries) {
      if (entry.respawnTimer) {
        clearTimeout(entry.respawnTimer);
      }
    }
    this.entries = [];
  }
}
